import { forwardRef, type CSSProperties, type ButtonHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type InputHTMLAttributes } from "react";
import { Link } from "react-router-dom";

export function cx(...c: Array<string | false | null | undefined>): string {
  return c.filter(Boolean).join(" ");
}

export function Card({ children, className, style, as: As = "section" }: { children: ReactNode; className?: string; style?: CSSProperties; as?: "section" | "div" | "article" | "aside" }) {
  return <As className={cx("card", className)} style={style}>{children}</As>;
}

export function CardHeader({ kicker, title, action, className }: { kicker?: ReactNode; title: ReactNode; action?: ReactNode; className?: string }) {
  return (
    <header className={cx("flex flex-wrap items-end justify-between gap-3 border-b border-line px-4 py-3 sm:px-5", className)}>
      <div className="min-w-0">
        {kicker && <div className="kicker mb-1">{kicker}</div>}
        <h2 className="display truncate text-[1.45rem] text-ink">{title}</h2>
      </div>
      {action && <div className="min-w-0 max-w-full text-sm">{action}</div>}
    </header>
  );
}

export function PageHeader({ kicker, title, lede, children, className }: { kicker?: ReactNode; title: ReactNode; lede?: ReactNode; children?: ReactNode; className?: string }) {
  return (
    <div className={cx("hardwood -mx-4 mb-6 border-b border-line px-4 pb-7 pt-8 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8", className)}>
      <div className="mx-auto max-w-[1400px]">
        {kicker && <div className="kicker mb-3 text-accent-text">{kicker}</div>}
        <h1 className="display text-[2.6rem] text-ink sm:text-[3.6rem]">{title}</h1>
        {lede && <p className="mt-3 max-w-3xl text-[1.05rem] text-muted">{lede}</p>}
        {children && <div className="mt-5">{children}</div>}
      </div>
    </div>
  );
}

type BadgeTone = "neutral" | "accent" | "pos" | "neg" | "warn" | "d1" | "d2" | "d3" | "d4" | "live";
const toneClass: Record<BadgeTone, string> = {
  neutral: "border-line-strong text-muted",
  accent: "border-accent/50 text-accent-text bg-accent-soft",
  pos: "border-pos/40 text-pos",
  neg: "border-neg/40 text-neg",
  warn: "border-warn/40 text-warn",
  d1: "border-d1/40 text-d1",
  d2: "border-d2/40 text-d2",
  d3: "border-d3/40 text-d3",
  d4: "border-d4/40 text-d4",
  live: "border-neg/60 text-neg bg-neg/10",
};

export function Badge({ children, tone = "neutral", className, title }: { children: ReactNode; tone?: BadgeTone; className?: string; title?: string }) {
  return (
    <span title={title} className={cx("inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-mono text-[0.65rem] uppercase tracking-[0.08em]", toneClass[tone], className)}>
      {children}
    </span>
  );
}

export const Button = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement> & { variant?: "primary" | "ghost" | "outline" | "danger"; size?: "sm" | "md" }>(
  function Button({ variant = "outline", size = "md", className, ...props }, ref) {
    return (
      <button
        ref={ref}
        {...props}
        className={cx(
          "inline-flex items-center justify-center gap-2 rounded-lg font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50",
          size === "sm" ? "h-8 px-3 text-sm" : "h-10 px-4 text-[0.95rem]",
          variant === "primary" && "bg-accent text-accent-ink hover:brightness-110",
          variant === "outline" && "border border-line-strong bg-surface text-ink hover:border-accent hover:text-accent-text",
          variant === "ghost" && "text-muted hover:bg-surface-2 hover:text-ink",
          variant === "danger" && "border border-neg/50 text-neg hover:bg-neg/10",
          className,
        )}
      />
    );
  },
);

export function Select({ label, className, children, ...props }: SelectHTMLAttributes<HTMLSelectElement> & { label?: string }) {
  const sel = (
    <select
      {...props}
      className={cx(
        "h-9 w-full appearance-none rounded-lg border border-line-strong bg-surface-2 bg-[length:10px] bg-[right_10px_center] bg-no-repeat pl-3 pr-8 text-sm text-ink hover:border-faint",
        "bg-[url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%239aa1ad' stroke-width='1.5' fill='none'/%3E%3C/svg%3E\")]",
        className,
      )}
    >
      {children}
    </select>
  );
  if (!label) return sel;
  return (
    <label className="flex min-w-0 flex-col gap-1">
      <span className="kicker">{label}</span>
      {sel}
    </label>
  );
}

export function Input({ label, className, ...props }: InputHTMLAttributes<HTMLInputElement> & { label?: string }) {
  const input = (
    <input
      {...props}
      className={cx("h-9 w-full rounded-lg border border-line-strong bg-surface-2 px-3 text-sm text-ink placeholder:text-faint hover:border-faint", className)}
    />
  );
  if (!label) return input;
  return (
    <label className="flex min-w-0 flex-col gap-1">
      <span className="kicker">{label}</span>
      {input}
    </label>
  );
}

export function Segmented<T extends string>({ value, onChange, options, label, size = "md" }: {
  value: T; onChange: (v: T) => void; options: Array<{ value: T; label: ReactNode; title?: string }>; label: string; size?: "sm" | "md";
}) {
  return (
    <div role="radiogroup" aria-label={label} className="inline-flex flex-wrap rounded-lg border border-line-strong bg-surface-2 p-0.5">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          title={o.title}
          onClick={() => onChange(o.value)}
          className={cx(
            "rounded-md font-medium transition-colors",
            size === "sm" ? "px-2.5 py-1 text-xs" : "px-3 py-1.5 text-sm",
            value === o.value ? "bg-ink text-bg" : "text-muted hover:text-ink",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function StatTile({ label, value, sub, tone }: { label: ReactNode; value: ReactNode; sub?: ReactNode; tone?: "accent" | "pos" | "neg" }) {
  return (
    <div className="rounded-xl border border-line bg-surface-2 px-4 py-3">
      <div className="kicker">{label}</div>
      <div className={cx("num mt-1 text-2xl font-semibold", tone === "accent" ? "text-accent-text" : tone === "pos" ? "text-pos" : tone === "neg" ? "text-neg" : "text-ink")}>{value}</div>
      {sub && <div className="mt-0.5 text-xs text-muted">{sub}</div>}
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div aria-hidden className={cx("skeleton", className)} />;
}

export function SkeletonRows({ rows = 6, className }: { rows?: number; className?: string }) {
  return (
    <div className={cx("space-y-2 p-4", className)} aria-busy="true" aria-live="polite">
      <span className="sr-only">Chargement…</span>
      {Array.from({ length: rows }, (_, i) => <Skeleton key={i} className="h-7 w-full" />)}
    </div>
  );
}

export function TextLink({ to, children, className }: { to: string; children: ReactNode; className?: string }) {
  return (
    <Link to={to} className={cx("font-medium text-ink underline decoration-line-strong decoration-1 underline-offset-4 hover:text-accent-text hover:decoration-accent", className)}>
      {children}
    </Link>
  );
}

export function MoreLink({ to, children = "Tout voir" }: { to: string; children?: ReactNode }) {
  return (
    <Link to={to} className="font-mono text-[0.7rem] uppercase tracking-[0.12em] text-muted hover:text-accent-text">
      {children} →
    </Link>
  );
}

export function Disclosure({ summary, children, className }: { summary: ReactNode; children: ReactNode; className?: string }) {
  return (
    <details className={cx("group rounded-lg border border-line bg-surface-2 px-3 py-2 text-sm", className)}>
      <summary className="cursor-pointer list-none font-medium text-ink-2 marker:hidden">
        <span className="mr-1 inline-block transition-transform group-open:rotate-90">›</span> {summary}
      </summary>
      <div className="mt-2 text-muted">{children}</div>
    </details>
  );
}
